import { useMemo, useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/pages/home/components/MovieCard';
import { moviesData, categoryInfo } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';

type CategoryKey = 'movie' | 'tv' | 'variety' | 'anime';

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const categoryKey = (type || 'movie') as CategoryKey;

  const [activeSubCategory, setActiveSubCategory] = useState('全部');
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'latest'>('latest');

  const cat = categoryInfo[categoryKey] || categoryInfo.movie;

  const filteredMovies = useMemo(() => {
    let result = moviesData.filter((m) => m.category === categoryKey);

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(q) ||
          m.description.toLowerCase().includes(q) ||
          m.actors.some((a) => a.toLowerCase().includes(q)) ||
          m.director.toLowerCase().includes(q)
      );
    }

    if (activeSubCategory !== '全部') {
      result = result.filter((m) => m.subCategory.includes(activeSubCategory));
    }

    result = [...result].sort((a, b) => {
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'year') return b.year - a.year;
      return 0;
    });

    return result;
  }, [categoryKey, searchQuery, activeSubCategory, sortBy]);

  const subCategories = useMemo(() => {
    const all = moviesData
      .filter((m) => m.category === categoryKey)
      .flatMap((m) => m.subCategory.split('/'));
    const unique = ['全部', ...Array.from(new Set(all))];
    return unique;
  }, [categoryKey]);

  useEffect(() => {
    setActiveSubCategory('全部');
    setSortBy('latest');
  }, [categoryKey]);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: cat.name },
  ];

  const categoryNames: Record<string, string> = {
    movie: '电影',
    tv: '电视剧',
    variety: '综艺',
    anime: '动漫',
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `90影院 - ${cat.name}分类`,
          description: `浏览90影院${cat.name}分类下的所有精彩影视内容。${cat.description}`,
          url: `https://www.90cinema.com/category/${categoryKey}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '90影院',
            url: 'https://www.90cinema.com/',
          },
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: filteredMovies.slice(0, 20).map((movie, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@type': 'Movie',
                name: movie.title,
                url: `https://www.90cinema.com/detail/${movie.id}`,
              },
            })),
          },
        })}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb items={breadcrumbItems} />

        <main>
          <section className="w-full px-4 md:px-6 py-5 md:py-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className={`${cat.icon} text-lg`}></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  {cat.name}
                </h1>
                <p className="text-sm text-foreground-500">{cat.description}</p>
              </div>
            </div>
          </section>

          {searchQuery && (
            <section className="w-full px-4 md:px-6 pb-3">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-50 text-primary-700 text-sm">
                <i className="ri-search-line"></i>
                搜索：{searchQuery}
                <button
                  onClick={() => {
                    window.REACT_APP_NAVIGATE(`/category/${categoryKey}`);
                  }}
                  className="ml-1 w-5 h-5 flex items-center justify-center rounded hover:bg-primary-100 cursor-pointer"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </div>
            </section>
          )}

          <section className="w-full px-4 md:px-6 pb-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center flex-wrap gap-2">
                {subCategories.map((sub) => (
                  <button
                    key={sub}
                    onClick={() => setActiveSubCategory(sub)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeSubCategory === sub
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {sub}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                {(['latest', 'rating', 'year'] as const).map((sort) => {
                  const labels = { latest: '最新', rating: '评分', year: '年份' };
                  return (
                    <button
                      key={sort}
                      onClick={() => setSortBy(sort)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        sortBy === sort
                          ? 'bg-secondary-200 text-secondary-800'
                          : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                      }`}
                    >
                      {labels[sort]}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 pb-10">
            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4 md:gap-5">
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">暂无相关影视内容</p>
                {searchQuery && (
                  <button
                    onClick={() => {
                      window.REACT_APP_NAVIGATE(`/category/${categoryKey}`);
                    }}
                    className="mt-3 px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    返回分类页面
                  </button>
                )}
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}